import { AdminKeysSecretsEnvironmentCredentials } from "../../models/admin-keys-secrets.models";
import AdminKeysSecretsCredentialValue from "./AdminKeysSecretsCredentialValue";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { twMerge } from "tailwind-merge";

type Props = {
  environmentCredentials: AdminKeysSecretsEnvironmentCredentials;
};

function formatReceivedAt(value?: string) {
  if (!value) {
    return "Missing";
  }

  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return value;
  }

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp));
}

function getStatusLabel(status: AdminKeysSecretsEnvironmentCredentials["status"]) {
  if (status === "complete") {
    return "Complete";
  }

  if (status === "missing_access_key") {
    return "Missing Access Key";
  }

  if (status === "missing_secret_key") {
    return "Missing Secret Key";
  }

  if (status === "scan_error") {
    return "Scan error";
  }

  return "Missing pair";
}

const AdminKeysSecretsEnvironmentRow = ({ environmentCredentials }: Props) => {
  const isComplete = environmentCredentials.status === "complete";

  return (
    <tr
      data-test={`admin-keys-secrets-row-${environmentCredentials.displayEnvironment}`}
      className="border-b border-[#D7E2DE] last:border-b-0 dark:border-gray-700"
    >
      <th scope="row" className="w-32 px-4 py-3 text-left align-top text-xs font-semibold uppercase text-green">
        {environmentCredentials.displayEnvironment}
      </th>
      <td className="min-w-[220px] px-4 py-3 align-top text-xs font-semibold text-[#4A5655] dark:text-gray-400">
        {environmentCredentials.iamUser}
      </td>
      <td className="min-w-[220px] px-4 py-3 align-top">
        <AdminKeysSecretsCredentialValue preview={environmentCredentials.accessKeyPreview} />
        <span className="mt-1 block text-[11px] text-[#637381] dark:text-gray-500">
          {formatReceivedAt(environmentCredentials.accessKeyReceivedAt)}
        </span>
      </td>
      <td className="min-w-[300px] px-4 py-3 align-top">
        <AdminKeysSecretsCredentialValue preview={environmentCredentials.secretKeyPreview} />
        <span className="mt-1 block text-[11px] text-[#637381] dark:text-gray-500">
          {formatReceivedAt(environmentCredentials.secretKeyReceivedAt)}
        </span>
      </td>
      <td className="w-40 px-4 py-3 align-top">
        <span
          className={twMerge(
            "inline-flex items-center gap-1 border px-2 py-1 text-[11px] font-semibold",
            isComplete
              ? "border-[#B7C9C4] bg-[#E9F4F3] text-green dark:border-gray-600 dark:bg-gray-800"
              : "border-[#F59692] bg-[#FFEAED] text-[#BF0002] dark:border-red-700 dark:bg-gray-800",
          )}
          data-test={`admin-keys-secrets-status-${environmentCredentials.displayEnvironment}`}
        >
          {!isComplete && <ExclamationTriangleIcon className="h-3.5 w-3.5" />}
          {getStatusLabel(environmentCredentials.status)}
        </span>
      </td>
    </tr>
  );
};

export default AdminKeysSecretsEnvironmentRow;
